import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { App as AntdApp, Button, Popconfirm } from 'antd';

import GroupHeader from '../common/GroupHeader';
import TransferOfferSummary, { offeredBy } from '../transfers/TransferOfferSummary';
import {
	acceptTransferOffer,
	declineTransferOffer,
	listTransferOffers,
	transferOfferRefusal,
} from '../../services/transferOffers';
import { useOnSessionChange } from '../../utils/session';

/**
 * Home's "Offered to you" (KPR-743): the collections someone offers this
 * person, each with Accept and Decline, and the door to the offer page. The
 * section exists only while an offer waits: it draws nothing until the list
 * arrives, and nothing when the read fails (the notice in the inbox carries
 * the same buttons), so Home's own states are never held up by it.
 */
export default function HomeTransferOffers() {
	const { message } = AntdApp.useApp();
	const [offers, setOffers] = useState([]);
	const [busy, setBusy] = useState(null); // `${verb}:${id}`
	const requestRef = useRef(0);

	const load = useCallback(() => {
		const request = ++requestRef.current;
		listTransferOffers()
			.then(({ incoming }) => { if (request === requestRef.current) setOffers(incoming); })
			.catch(() => { if (request === requestRef.current) setOffers([]); });
	}, []);

	useEffect(() => {
		load();
		return () => { requestRef.current += 1; };
	}, [load]);
	useOnSessionChange(load);

	const act = async (verb, offer) => {
		setBusy(`${verb}:${offer._id}`);
		try {
			if (verb === 'accept') {
				await acceptTransferOffer(offer._id);
				message.success(`You own ${offer.collection?.name || 'the collection'} now.`);
			} else {
				await declineTransferOffer(offer._id);
				message.success('Declined. Nothing changed.');
			}
		} catch (error) {
			message.error(transferOfferRefusal(error));
		} finally {
			setBusy(null);
			load();
		}
	};

	if (!offers.length) return null;

	return (
		<section className="home-page__section" aria-label="Offered to you">
			<GroupHeader label="Offered to you" count={offers.length} />
			<ul className="home-transfer-offers">
				{offers.map(offer => (
					<li key={offer._id} className="home-transfer-offers__row" data-testid="home-transfer-offer">
						<div className="home-transfer-offers__main">
							<TransferOfferSummary offer={offer} size="sm" />
							<p className="home-transfer-offers__from">
								{`From ${offeredBy(offer)}`}
								{' · '}
								<Link to={`/transfers/${offer._id}`}>See the offer</Link>
							</p>
						</div>
						<div className="home-transfer-offers__actions">
							<Button
								onClick={() => act('decline', offer)}
								loading={busy === `decline:${offer._id}`}
								disabled={Boolean(busy)}
							>
								Decline
							</Button>
							<Popconfirm
								title={`Accept ${offer.collection?.name || 'this collection'}?`}
								description="You become its owner and responsible for what is in it."
								okText="Accept"
								cancelText="Not now"
								onConfirm={() => act('accept', offer)}
							>
								<Button loading={busy === `accept:${offer._id}`} disabled={Boolean(busy)}>Accept…</Button>
							</Popconfirm>
						</div>
					</li>
				))}
			</ul>
		</section>
	);
}
